"use client"

import clsx from "clsx"
import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
import Image, { ImageProps } from "next/image"
import React, { useMemo, useState } from "react"

import Arrow from "../../../public/icons/arrow-up.svg"

import Button, { ButtonProps } from "./Button"
import PageGrid from "./PageGrid"

export type HoverItem = {
  title: string
  subheading?: React.ReactNode
  ctaLink?: string
  ctaText?: string
  description: React.ReactNode
  imageDesktop: ImageProps
  imageMobile: ImageProps
}

export type HoverLinkProps = {
  sectionHeader: string
  wrapperClassNames?: string
  headClassNames?: string
  hoverClassNames?: string
  headerColor?: string
  buttonTextColor?: string
  primaryButtonLink?: string
  primaryButtonCTA?: string
  tabs?: string[]
  links: Array<{
    button: ButtonProps
    items: HoverItem[]
  }>
}

const HoverLinks: React.FC<HoverLinkProps> = ({
  wrapperClassNames,
  headClassNames,
  hoverClassNames,
  headerColor,
  buttonTextColor,
  sectionHeader,
  links,
  primaryButtonLink,
  primaryButtonCTA,
  tabs,
}) => {
  const [activeLinkIndex, setActiveLinkIndex] = useState(0)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [showMobileLinks, setShowMobileLinks] = useState(false)
  const openMenuVariants: Variants = {
    block: {
      opacity: 1,
      display: "block",
      transition: {
        duration: 0.2,
        when: "beforeChildren",
      },
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.2,
        when: "afterChildren",
      },
      transitionEnd: {
        display: "none",
      },
    },
  }

  const activeTabGroup = useMemo(
    () => links[activeTabIndex],
    [activeTabIndex, links],
  )
  const activeTabItems = activeTabGroup?.items || []
  const activeTabButton = activeTabGroup?.button || null
  const slideMenuVariants: Variants = {
    block: {
      transition: {
        duration: 0.2,
        staggerChildren: 0.1,
      },
    },
    hidden: {
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  }

  const navItemVariants: Variants = {
    block: {
      opacity: 1,
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <section className={clsx(wrapperClassNames, "py-17.5 md:py-16 xl:py-28")}>
      <PageGrid>
        <h3
          className={clsx(
            "px-4 py-2 headline5 lg:headline4 col-span-full text-left",
            headClassNames,
          )}
        >
          {sectionHeader}
        </h3>
        {tabs && (
          <div className="col-span-full flex mt-8 lg:mt-12">
            {/* Desktop Tab Options */}
            <div className="hidden lg:flex w-full justify-center">
              {tabs.map((tab, index) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    setActiveTabIndex(index)
                    setActiveLinkIndex(0)
                  }}
                  className={clsx(
                    "mx-2 md:mx-4 py-2 headline5 border-b-2 whitespace-nowrap border-ripple-1",
                    index === activeTabIndex && "border-ripple-2",
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>
            {/* Mobile Tab Options */}
            <div className="lg:hidden relative w-full">
              <button
                type="button"
                className="w-full p-2 heading2 border-b-2 border-ripple-1 flex items-center"
                onClick={() => {
                  setShowMobileLinks(!showMobileLinks)
                }}
              >
                <span>{tabs[activeTabIndex]}</span>
                <Arrow
                  title="Arrow icon"
                  className={clsx(
                    "ml-4 transform duration-300",
                    !showMobileLinks && "rotate-180",
                  )}
                />
              </button>
              <motion.div
                variants={openMenuVariants}
                initial="hidden"
                animate={showMobileLinks ? "block" : "hidden"}
                className="absolute top-full left-0 w-full bg-blue-2.5 shadow-lg rounded-b-lg z-40"
              >
                <div className="top-0 left-0 shadow-md">
                  <PageGrid>
                    <motion.ul
                      variants={slideMenuVariants}
                      className="py-4 col-span-full"
                    >
                      {tabs.map(
                        (tab, index) =>
                          index !== activeTabIndex && (
                            <motion.li
                              variants={navItemVariants}
                              key={tab}
                              className="w-full p-2 heading2 text-left hover:text-blue-60 hover:cursor-pointer"
                              onClick={() => {
                                setActiveTabIndex(index)
                                setActiveLinkIndex(0)
                                setShowMobileLinks(false)
                              }}
                            >
                              {tab}
                            </motion.li>
                          ),
                      )}
                    </motion.ul>
                  </PageGrid>
                </div>
              </motion.div>
            </div>
          </div>
        )}
        <div
          className={clsx(
            "grid grid-cols-1 lg:col-span-6 col-span-full lg:block md:grid-cols-4 md:gap-x-8 lg:gap-x-0 lg:content-center mt-10 md:mt-8 lg:mt-20 lg:divide-y lg:divide-blue-60 border-blue-60 border-t-0 border-b-0 lg:border-t-1 lg:border-b-1",
          )}
        >
          {activeTabItems?.map((link, index) => {
            const {
              ctaLink,
              description,
              ctaText = "Learn More",
              title,
              imageMobile,
              subheading,
            } = link

            if (![title, imageMobile].every(Boolean)) {
              return null
            }

            return (
              <div
                key={title}
                className={clsx(
                  index === activeLinkIndex ? hoverClassNames : "",
                  "lg:col-auto md:col-span-2 col-span-1 lg:mb-0 md:mb-9 mb-14 hover:cursor-default",
                )}
                onMouseEnter={() => {
                  setActiveLinkIndex(index)
                }}
              >
                {imageMobile && (
                  <div className="mb-6 lg:hidden md:mb-4">
                    <Image
                      {...imageMobile}
                      className={clsx("rounded-lg", imageMobile?.className)}
                    />
                  </div>
                )}

                <div className="px-4 py-6">
                  <h4 className={clsx("heading1 lg:headline5", headerColor)}>
                    {title}
                  </h4>
                  {subheading && (
                    <div className="mt-4 heading2 md:mt-6">{subheading}</div>
                  )}
                  <div
                    className={clsx(
                      "body3",
                      ctaLink ? "py-4 md:py-6" : "pt-4 md:pt-6",
                    )}
                  >
                    {description}
                  </div>
                  {ctaLink && (
                    <Button
                      className={buttonTextColor}
                      variant="tertiary"
                      to={ctaLink}
                    >
                      {ctaText}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <div className="relative hidden lg:col-span-6 lg:block lg:content-center mt-10 md:mt-8 lg:mt-20">
          {activeTabItems?.map((link, index) => {
            const { imageDesktop } = link

            if (!imageDesktop) {
              return null
            }

            const { className } = imageDesktop

            return (
              <div
                key={link.title}
                className={clsx(
                  index !== activeLinkIndex && "invisible",
                  "absolute left-0 top-0 bottom-0 right-0 w-full h-full overflow-hidden rounded-lg",
                )}
              >
                <Image
                  {...imageDesktop}
                  className={clsx(className, "rounded-lg")}
                />
              </div>
            )
          })}
        </div>
        {activeTabButton?.to && activeTabButton?.children && (
          <div className="mt-8 col-span-full lg:mt-12">
            <Button {...activeTabButton} />
          </div>
        )}
        {primaryButtonLink && !activeTabButton?.to && (
          <div className="mt-8 col-span-full lg:mt-12">
            <Button to={primaryButtonLink}>{primaryButtonCTA}</Button>
          </div>
        )}
      </PageGrid>
    </section>
  )
}

export default HoverLinks
